import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import ModalOpenLoader from 'component/loader_component/modalOpenLoader';
import { SweetSweetAlert } from 'component/sweetalert_components';
import { Form, Input, Select } from 'antd';
import { StyledButton } from 'component/button_components/button_general/ButtonGeneralStyle';
import { getDomainData, updateDomain } from 'redux/slices/domainSlice';
import {
  FormHeader,
  FormItem,
  FormSubmitButtonConatainer,
  ModalFormContainer,
  RowWrapper,
} from 'component/form_component/style';
import Swal from 'sweetalert2';
import { FigCaption } from 'pages/license/style';
import { InputPatternRule, InputPatternRuleDomainAdminDn } from 'utils/input_pattern';

function UpdateDomain({ singleDomainData, setOpen, showModal, handleCancel, handlelogout }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  let domainChanged = false;
  const dropDownGroupData = useSelector((state) => state.getOuGroupData.dropDownData);

  const checkIPandUserName = async (values) => {
    const result = await Swal.fire({
      icon: 'info',
      title: `The Domain IP Address is modified, the existing Admin Groups will be cleared.</br>Please update the Admin Groups after a successful connection.`,
      showConfirmButton: true,
      confirmButtonColor: 'var(--bg-button-default-color)',
      width: '500px',
      heightAuto: false,
      showCancelButton: true,
      cancelButtonColor: 'green',
      confirmButtonText: 'Connect',
      cancelButtonText: 'Cancel',
      reverseButtons: true,
      customClass: {
        container: 'custom-swal-container',
        icon: 'custom-swal-icon',
        title: 'custom-swal-title',
      },
    });

    return result.isConfirmed;
  };
  useEffect(() => {
    if (singleDomainData) {
      form.setFieldsValue({
        domainName: singleDomainData?.domainName,
        fqdn: singleDomainData?.fqdn,
        ipAddress: singleDomainData?.ipAddress,
        username: singleDomainData?.username,
        password: '',
        superAdmin: singleDomainData?.superAdmin || null,
        ouAdmin: singleDomainData?.ouAdmin || null,
        securityType: singleDomainData?.securityType,
        port: singleDomainData?.port,
        passwordThreshold: singleDomainData?.passwordThreshold,
        portalAdmin: singleDomainData?.portalAdmin || null,
      });
    }
  }, [singleDomainData]);

  const handleUpdateDomain = async (values) => {
    if (values.ipAddress !== singleDomainData?.ipAddress) {
      const canProceed = await checkIPandUserName(values);
      domainChanged = canProceed;
      if (canProceed) {
        values.superAdmin = '';
        values.ouAdmin = '';
        values.portalAdmin = '';
      } else {
        return;
      }
    } else {
      values.superAdmin = values.superAdmin ? values.superAdmin : singleDomainData?.superAdmin;
      values.ouAdmin = values.ouAdmin ? values.ouAdmin : '';
      values.portalAdmin = values.portalAdmin ? values.portalAdmin : '';
    }
    values.passwordThreshold = parseInt(values.passwordThreshold);
    values.port = parseInt(values.port);
    values.isPrimaryDomain = singleDomainData?.isPrimaryDomain;
    setLoading(true);
    try {
      const value = { id: singleDomainData?.id, values };
      const sweetAlert = await dispatch(updateDomain(value));
      if (sweetAlert.payload && sweetAlert.payload.status === 200 && singleDomainData?.isPrimaryDomain === true) {
        if (!domainChanged && values.superAdmin !== singleDomainData?.superAdmin) {
          if (
            sweetAlert.payload.data === 'Domain name is already in use.' ||
            sweetAlert.payload.data === 'The Connection Fails. Please check your details and retry.' ||
            sweetAlert.payload.data === 'FQDN is already in use.' ||
            sweetAlert.payload.data === 'IPAddress is already in use.'
          ) {
            SweetSweetAlert({ sweetData: sweetAlert.payload.data, icon: 'info', buttonColor: 'blue' });
          } else {
            await SweetSweetAlert({
              sweetData: 'Your SuperAdmin group has been changed. You have to login back now.',
              icon: 'success',
              buttonColor: 'green',
            });
            handlelogout();
          }
        } else {
          await SweetSweetAlert({ sweetData: sweetAlert.payload.data, icon: 'success', buttonColor: 'green' });
          if (!domainChanged) {
            handleCancel();
            navigate('/adminpage');
            dispatch(getDomainData());
          }
        }
      } else if (sweetAlert.payload && sweetAlert.payload.status === 200) {
        if (
          sweetAlert.payload.data === 'Domain name is already in use.' ||
          sweetAlert.payload.data === 'The Connection Fails. Please check your details and retry.' ||
          sweetAlert.payload.data === 'FQDN is already in use.' ||
          sweetAlert.payload.data === 'IPAddress is already in use.'
        ) {
          SweetSweetAlert({ sweetData: sweetAlert.payload.data, icon: 'info', buttonColor: 'blue' });
        } else {
          SweetSweetAlert({ sweetData: sweetAlert.payload.data, icon: 'success', buttonColor: 'green' });
          if (!domainChanged) {
            handleCancel();
            navigate('/adminpage');
            dispatch(getDomainData());
          }
        }
      } else {
        SweetSweetAlert({ text: 'Something Wrong Please try again.!!', icon: 'error', buttonColor: 'red' });
      }
    } catch (error) {
      SweetSweetAlert({ text: 'Something Wrong Please try again.!!', icon: 'error', buttonColor: 'red' });
    } finally {
      setLoading(false);
      if (domainChanged) {
        setOpen(true);
        showModal();
        dispatch(getDomainData());
      }
    }
  };

  const handleSecurityTypeChange = (value) => {
    const newPort = value === 'SSL' ? 636 : 389;
    form.setFieldsValue({ port: newPort });
    if (value === 'Plain') {
      SweetSweetAlert({
        text: `Consider using SSL/TLS as Active Directory doesn’t allow password reset with plain protocol.`,
        icon: 'info',
        buttonColor: 'blue',
      });
    }
  };

  return (
    <ModalFormContainer className='af-modal-container af-updatedomain-modal-container'>
      {loading && <ModalOpenLoader />}
      <FormHeader className='af-form-header af-update_domain-form-header'>Update Domain</FormHeader>
      <Form
        className='af-form af-update_domain-form'
        form={form}
        layout='vertical'
        name='register'
        onFinish={handleUpdateDomain}
        scrollToFirstError
        initialValues={{
          domainName: singleDomainData?.domainName,
          fqdn: singleDomainData?.fqdn,
          ipAddress: singleDomainData?.ipAddress,
          username: singleDomainData?.username,
          password: '',
          superAdmin: singleDomainData?.superAdmin ? singleDomainData.superAdmin : null,
          ouAdmin: singleDomainData?.ouAdmin ? singleDomainData.ouAdmin : null,
          securityType: singleDomainData?.securityType,
          port: singleDomainData?.port,
          passwordThreshold: singleDomainData?.passwordThreshold,
          portalAdmin: singleDomainData?.portalAdmin ? singleDomainData.portalAdmin : null,
        }}
        size='large'
      >
        <RowWrapper className='af-form-row-wraper af-update_domain-form-row-wraper af-update_domain-form-row-wraper-wrap1'>
          <FormItem
            className='af-form-item af-update_domain-form-item af-update_domain-form-item-domain_name'
            key='domainName'
            name='domainName'
            label='Domain Name'
            rules={[
              {
                required: true,
                message: `Please Enter the Domain Name`,
              },
              {
                pattern: InputPatternRule.pattern,
                message: InputPatternRule.message,
              },
            ]}
          >
            <Input className='af-form-input af-update_domain-form-input af-update_domain-form-input_domain_name' />
          </FormItem>
          <FormItem
            key='fqdn'
            name='fqdn'
            label='FQDN'
            rules={[
              {
                required: true,
                message: `Please Enter the FQDN`,
              },
              {
                pattern: InputPatternRule.pattern,
                message: InputPatternRule.message,
              },
            ]}
            className='af-form-item af-update_domain-form-item af-update_domain-form-item-fqdn'
          >
            <Input className='af-form-input af-update_domain-form-input af-update_domain-form-input_fqdn' />
          </FormItem>
        </RowWrapper>
        <RowWrapper className='af-form-row-wraper af-update_domain-form-row-wraper af-update_domain-form-row-wraper-wrap2'>
          <FormItem
            key='ipAddress'
            name='ipAddress'
            label='Server Name/IP'
            rules={[
              {
                required: true,
                message: `Please Enter the Server Name/IP`,
              },
            ]}
            className='af-form-item af-update_domain-form-item af-update_domain-form-item-ip_address'
          >
            <Input />
          </FormItem>

          <FormItem
            key='username'
            name='username'
            label='Admin User (DN)'
            rules={[
              {
                required: true,
                message: `Please Enter the Admin User (DN)`,
              },
              {
                pattern: InputPatternRuleDomainAdminDn.pattern,
                message: InputPatternRuleDomainAdminDn.message,
              },
            ]}
            className='af-form-item af-update_domain-form-item af-update_domain-form-item-username'
          >
            <Input className='af-form-input af-update_domain-form-input af-update_domain-form-input_username' />
          </FormItem>
        </RowWrapper>
        <RowWrapper className='af-form-row-wraper af-update_domain-form-row-wraper af-update_domain-form-row-wraper-wrap3'>
          <FormItem
            key='password'
            name='password'
            label='Password'
            rules={[
              {
                required: true,
                message: `Please Enter the Password`,
              },
            ]}
            className='af-form-item af-update_domain-form-item af-update_domain-form-item-password'
          >
            <Input.Password
              visibilityToggle={false}
              className='af-form-input af-update_domain-form-input af-update_domain-form-input_password'
            />
          </FormItem>
          <FormItem
            className='af-form-item af-update_domain-form-item af-update_domain-form-item-security_type'
            key='securityType'
            name='securityType'
            label='Security Type'
          >
            <Select
              className='af-form-select af-update_domain-form-select af-update_domain-form-select_security_type'
              showSearch
              placeholder='Select'
              defaultValue='Plain'
              onChange={handleSecurityTypeChange}
            >
              <Select.Option className='af-form-select-option af-update_domain-form-select-option' value='Plain'>
                Plain
              </Select.Option>
              <Select.Option className='af-form-select-option af-update_domain-form-select-option' value='SSL'>
                SSL
              </Select.Option>
              <Select.Option className='af-form-select-option af-update_domain-form-select-option' value='TLS'>
                TLS
              </Select.Option>
            </Select>
          </FormItem>
        </RowWrapper>
        <RowWrapper className='af-form-row-wraper af-update_domain-form-row-wraper af-update_domain-form-row-wraper-wrap4'>
          <FormItem
            className='af-form-item af-update_domain-form-item af-update_domain-form-item-port'
            key='port'
            name='port'
            label='Port'
          >
            <Input
              className='af-form-input af-update_domain-form-input af-update_domain-form-input_port'
              type='number'
            />
          </FormItem>
          <div
            className='update_domain-form-input_password_threshold-wrapper'
            style={{ width: '100%', marginBottom: 24 }}
          >
            <FormItem
              key='threshold'
              name='passwordThreshold'
              label='Maximum Password Age'
              rules={[
                {
                  required: true,
                  message: 'Please enter the Threshold!',
                },
              ]}
              style={{ marginBottom: 0 }}
              className='af-form-item af-update_domain-form-item af-update_domain-form-item-password_threshold'
            >
              <Input
                type='number'
                className='af-form-input af-update_domain-form-input af-update_domain-form-input_password_threshold'
              />
            </FormItem>
            <FigCaption className='update_domain-caption update_domain-caption-threshold'>
              **Configure as per your AD group policy
            </FigCaption>
          </div>
        </RowWrapper>

        <RowWrapper className='af-form-row-wraper af-update_domain-form-row-wraper af-update_domain-form-row-wraper-wrap5'>
          <FormItem
            key='portalAdmin'
            name='portalAdmin'
            label='Domain Admin'
            className='af-form-item af-update_domain-form-item af-update_domain-form-item-portal_admin'
          >
            <Select
              className='af-form-select af-update_domain-form-select af-update_domain-form-select_portal_admin'
              showSearch
              placeholder='Select the PortalAdmin Group'
              allowClear
            >
              {dropDownGroupData &&
                dropDownGroupData.map((group) => (
                  <Select.Option
                    className='af-form-select-option af-update_domain-form-select-option'
                    key={group.distinguishedName}
                    value={group.distinguishedName}
                  >
                    {group.groupName}
                  </Select.Option>
                ))}
            </Select>
          </FormItem>
          <FormItem
            key='ouAdmin'
            name='ouAdmin'
            label='Ou Admin'
            className='af-form-item af-update_domain-form-item af-update_domain-form-item-ouadmin'
          >
            <Select
              className='af-form-select af-update_domain-form-select af-update_domain-form-select_ouadmin'
              showSearch
              placeholder='Select the OuAdmin Group'
              allowClear
            >
              {dropDownGroupData &&
                dropDownGroupData.map((group) => (
                  <Select.Option
                    className='af-form-select-option af-update_domain-form-select-option'
                    key={group.distinguishedName}
                    value={group.distinguishedName}
                  >
                    {group.groupName}
                  </Select.Option>
                ))}
            </Select>
          </FormItem>
        </RowWrapper>
        <RowWrapper className='af-form-row-wraper af-update_domain-form-row-wraper af-update_domain-form-row-wraper-wrap6'>
          {singleDomainData?.isPrimaryDomain && (
            <FormItem
              className='af-form-item af-update_domain-form-item af-update_domain-form-item-super_admin oddFormItem'
              key='superAdmin'
              name='superAdmin'
              label='Super Admin'
              rules={[
                {
                  required: true,
                  message: `Please Enter the Super Admin`,
                },
              ]}
            >
              <Select
                showSearch
                placeholder='Select the SuperAdmin Group '
                allowClear
                className='af-form-select af-update_domain-form-select af-update_domain-form-select_super_admin'
              >
                {dropDownGroupData &&
                  dropDownGroupData.map((group) => (
                    <Select.Option
                      className='af-form-select-option af-update_domain-form-select-option'
                      key={group.distinguishedName}
                      value={group.distinguishedName}
                    >
                      {group.groupName}
                    </Select.Option>
                  ))}
              </Select>
            </FormItem>
          )}
        </RowWrapper>
        <FormSubmitButtonConatainer className='af-form-button-submit-wrapper af-update_domain-form-button-submit-wrapper'>
          <StyledButton
            type='primary'
            htmlType='submit'
            className='af-form-button af-update_domain-form-button af-update_domain-form-button_submit'
          >
            Update
          </StyledButton>
        </FormSubmitButtonConatainer>
      </Form>
    </ModalFormContainer>
  );
}

export default UpdateDomain;
